"use client";

import { useEffect, useState } from "react";

interface Props {
  iso: string;
  /**
   * `date` (default) → toLocaleDateString. `datetime` → toLocaleString.
   * Both fire only client-side; SSR + first-hydration pass returns the
   * ISO date prefix (`YYYY-MM-DD`) so the rendered text is identical
   * between server and client. After mount we upgrade to the
   * browser-locale-aware format.
   */
  kind?: "date" | "datetime";
  /**
   * Optional Intl.DateTimeFormat options forwarded to
   * `toLocaleDateString`. Use this for partial-date displays like
   * "Joined Apr 2026" (`{month: "short", year: "numeric"}`). The
   * SSR-safe fallback for these custom-format cases is the ISO date
   * prefix — visually different for one frame, then upgrades on mount.
   */
  options?: Intl.DateTimeFormatOptions;
}

/**
 * 2026-05-04 hydration #418 fix — `Date.prototype.toLocaleDateString()`
 * (called with no args) reads Node's default locale on the server and
 * the browser's locale on the client. When those disagree (e.g. server
 * en-US vs client pt-BR), the rendered text differs character-by-
 * character and React aborts hydration with error #418
 * ("Hydration failed because the initial UI does not match what was
 * rendered on the server").
 *
 * The textbook fix is to defer locale-aware rendering until after
 * mount. SSR + first hydration use a deterministic ISO prefix
 * (`YYYY-MM-DD` or `YYYY-MM-DDTHH:MM`); the useEffect-gated upgrade
 * fires the locale formatter on the client. The visual flash is one
 * frame; the alternative (chopping interactivity for a beat while
 * React re-renders the whole tree client-side after a #418) is far
 * worse.
 *
 * Using this directly:
 *   <ClientLocaleDate iso={post.createdAt} />
 *   <ClientLocaleDate iso={market.resolvedAt} kind="datetime" />
 */
export function ClientLocaleDate({ iso, kind = "date", options }: Props) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Deterministic placeholder for SSR + first client render.
  const fallback =
    kind === "datetime" ? iso.slice(0, 16).replace("T", " ") : iso.slice(0, 10);

  if (!mounted) return <>{fallback}</>;

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return <>{fallback}</>;
  if (kind === "datetime") return <>{d.toLocaleString()}</>;
  return <>{d.toLocaleDateString(undefined, options)}</>;
}
